import { EventResult, ReporterFeedbackParticipantMapping } from '@/apiClient'
import {
  AttendeeRole,
  CNA_TYPE_OTHER_TEXT_CHAR_LIMIT,
  EXPEDITED_PENDING,
  mapToSupportedTurnaroundHours,
  TurnaroundHours,
} from '@/constants'
import type { EventFeedback as EventFeedbackPayload } from '@/queries/events'
import {
  EventFeedback,
  ReadWaiveAnswer,
  ReporterFeedback,
  TranscriptOrders,
} from '@/types'
import { COUNTIES, STATES_CODES } from '@/utils/states'
import { isValidEmail } from '@/utils/validations'

export const ORDERABLE_RESULTS: ReadonlySet<EventResult> = new Set<EventResult>(
  [EventResult.completed, EventResult.completed_no_show, EventResult.reset],
)

export const isOrderableResult = (
  result: EventResult | null | undefined,
  allowCnaTranscriptOrders = false,
): boolean =>
  !!result &&
  (ORDERABLE_RESULTS.has(result) ||
    (allowCnaTranscriptOrders && result === EventResult.canceled_cna))

export const buildEventFeedbackPayload = (
  feedback: EventFeedback,
  allowCnaTranscriptOrders = false,
  allowTranscriptOrders = true,
): EventFeedbackPayload => {
  const attendees = feedback.attendees.map(
    ({ id: _id, ...attendee }) => attendee,
  ) as EventFeedbackPayload['attendees']

  // CR Form (staged) mappings: read/waive -> witness_requested_transcript, witness
  // location from the witness attendee, and per-attendee orders (attendee_orders) into
  // the transcript_orders shape. Absent for non-staged feedback (fields undefined).
  const witness = feedback.attendees.find(
    ({ role }) => role === AttendeeRole.WITNESS,
  )
  // attendee name -> Zoom participant(s) they joined as (mirrors the DR Script's
  // _get_participants_mapping). Only attendees still marked present are included, so a
  // mapping left over from before an attendee was switched to "not present" isn't sent
  // (the value stays in form state so the dropdown refills if they're set present again).
  // Non-staged feedback (no zoom_participants) leaves participants_mapping undefined.
  const participantsMapping: Record<
    string,
    ReporterFeedbackParticipantMapping[]
  > = {}
  feedback.attendees.forEach(attendee => {
    const zoomParticipants = attendee.zoom_participants ?? []
    if (attendee.present === true && zoomParticipants.length > 0) {
      participantsMapping[attendee.name] = zoomParticipants.map(zp => ({
        user_id: zp.user_id,
        user_name: zp.user_name,
        meeting_instance_id: zp.meeting_instance_id,
        participant_user_id: zp.participant_user_id,
        join_time: zp.join_time,
      }))
    }
  })

  // 'na' means read/waive did not apply here, which parrot stores as "no answer"
  // and resolves against the state's rule -- never as the witness waiving.
  const readWaiveAnswer: ReadWaiveAnswer =
    feedback.read_waive === 'read'
      ? 'read'
      : feedback.read_waive === 'waive'
        ? 'waive'
        : feedback.read_waive === 'na'
          ? 'no_answer'
          : feedback.read_waive_answer ?? 'no_answer'

  const crFields: Partial<EventFeedbackPayload> = {
    read_waive_answer: readWaiveAnswer,
    // Only an explicit read/waive answer overrides the flag; 'na' (read/waive doesn't
    // apply) must not be misreported as the witness waiving signature.
    witness_requested_transcript:
      feedback.read_waive === 'read'
        ? true
        : feedback.read_waive === 'waive'
          ? false
          : feedback.witness_requested_transcript,
    witness_city: witness?.city ?? undefined,
    witness_state: witness?.state ?? undefined,
    participants_mapping: Object.keys(participantsMapping).length
      ? participantsMapping
      : undefined,
  }

  // Prefer the staged per-attendee orders only when they actually hold entries;
  // otherwise keep the existing transcript_orders (non-staged forms seed an empty map).
  // attendee_orders is keyed by attendee id; transcript_orders must be keyed by email
  // (the backend/legacy contract — parrot creates TranscriptOrder(email=...)).
  const attendeeOrders = feedback.attendee_orders ?? {}
  const emailByAttendeeKey = new Map<string, string>()
  feedback.attendees.forEach((attendee, i) => {
    if (attendee.email) {
      emailByAttendeeKey.set(attendee.id ?? String(i), attendee.email)
    }
  })
  const sourceOrders: TranscriptOrders | null =
    // Only build orders from the staged per-attendee map when orders are actually present;
    // otherwise stale selections left over after switching to "No" would still be sent.
    Object.keys(attendeeOrders).length > 0 && feedback.orders_present === true
      ? Object.entries(attendeeOrders).reduce<TranscriptOrders>(
          (orders, [attendeeKey, value]) => {
            const email = emailByAttendeeKey.get(attendeeKey)
            // Skip attendees without an email, with no selection, or with an incomplete
            // (pending) expedited order.
            if (
              email &&
              value !== null &&
              value !== '' &&
              value !== EXPEDITED_PENDING
            ) {
              // Map the (possibly faked) staged-form selection to a backend-supported
              // turnaround; identity for the existing 0/24/72/168 values.
              orders[email] = {
                turnaround_hours: mapToSupportedTurnaroundHours(
                  Number(value),
                ) as TurnaroundHours,
              }
            }
            return orders
          },
          {},
        )
      : feedback.transcript_orders

  if (
    !allowTranscriptOrders ||
    !isOrderableResult(feedback.result, allowCnaTranscriptOrders)
  ) {
    return {
      ...feedback,
      ...crFields,
      transcript_ordered: false,
      turnaround_hours: null,
      transcript_orders: null,
      attendees,
    }
  }

  const filteredOrders = sourceOrders
    ? Object.fromEntries(
        Object.entries(sourceOrders).filter(
          ([, order]) =>
            order.turnaround_hours !== null &&
            order.turnaround_hours !== TurnaroundHours.ROUGH_DRAFT,
        ),
      )
    : {}
  const hasOrders = Object.keys(filteredOrders).length > 0

  return {
    ...feedback,
    ...crFields,
    transcript_ordered: hasOrders,
    transcript_orders: hasOrders ? (filteredOrders as TranscriptOrders) : null,
    attendees,
  }
}

export const reporterStateValidation = (reporterState: string | null) => {
  if (reporterState !== null && STATES_CODES.includes(reporterState)) {
    return null
  }
  return 'Please pick valid state'
}

export const reporterCountyValidation = (
  county: string | null,
  values: ReporterFeedback,
) => {
  if (
    county !== null &&
    values !== undefined &&
    values.reporter_state !== null &&
    STATES_CODES.includes(values.reporter_state) &&
    COUNTIES[values.reporter_state].includes(county)
  ) {
    return null
  }
  return 'Please pick valid county'
}

export const transcriptOrderedValidation = (
  transcriptOrdered: boolean | null,
  values: EventFeedback,
  context: {
    orderRequired: boolean
  },
  allowCnaTranscriptOrders = false,
) => {
  if (
    context.orderRequired &&
    (values.result === 'completed' || values.result === 'completed_no_show') &&
    !transcriptOrdered
  ) {
    return 'Certified transcript must be ordered for completed event'
  }

  if (
    !isOrderableResult(values.result, allowCnaTranscriptOrders) &&
    transcriptOrdered
  ) {
    return "Certified transcript can't be ordered for not completed event"
  }

  return null
}

export const turnaroundHoursValidation = (
  turnaroundHours: number | null,
  values: EventFeedback,
  canOrderForOc: boolean,
) => {
  if (!values?.transcript_ordered || canOrderForOc) {
    return null
  }

  if (turnaroundHours !== null) {
    return null
  }

  return 'Please pick turnaround hours'
}

export const transcriptOrdersValidation = (
  transcriptOrders: TranscriptOrders | null,
  values: EventFeedback,
  context: {
    canOrderForOc: boolean
    isOrderRequired: boolean
  },
) => {
  if (!context.canOrderForOc) {
    return null
  }

  if (!values?.transcript_ordered) {
    return null
  }

  const orders = Object.values(transcriptOrders ?? {})

  // for court reporter events all attorneys must order transcript
  // for other events only some attorneys can order transcript
  if (!context.isOrderRequired) {
    // there should be at least one order, otherwise the `transcript_ordered` should be false
    if (
      orders.some(
        order =>
          order.turnaround_hours !== null && order.turnaround_hours !== 0,
      )
    ) {
      return null
    }
  } else {
    if (
      orders.every(
        order =>
          order.turnaround_hours !== null && order.turnaround_hours !== 0,
      )
    ) {
      return null
    }
  }

  return orders.length > 1
    ? `Please fill out ${context.isOrderRequired ? 'all transcription orders' : 'at least one transcription order'}`
    : 'Please fill out a transcription order'
}

export const cnaDeclaredAtValidation = (
  canDeclaredAt: string | null,
  values: EventFeedback,
) => {
  if (values?.result !== 'canceled_cna' || canDeclaredAt !== null) {
    return null
  }

  return 'Please pick valid time'
}

export const cnaTypeValidation = (
  cnaType: string | null,
  values: EventFeedback,
) => {
  if (values?.result !== 'canceled_cna') {
    return null
  }

  if (cnaType !== null) {
    return null
  }

  return 'Please pick type of CNA certificate'
}

export const cnaTypeOtherTextValidation = (
  cnaTypeText: string | null,
  values: EventFeedback,
) => {
  if (values?.result !== 'canceled_cna' || values?.cna_type !== 'other') {
    return null
  }

  if (
    cnaTypeText !== null &&
    cnaTypeText.length > 0 &&
    cnaTypeText.length <= CNA_TYPE_OTHER_TEXT_CHAR_LIMIT
  ) {
    return null
  }

  return 'Please specify the reason for the CNA certificate'
}

// Both skipped on BYOCR events: the orders question is hidden there, so an old draft's
// answer can't be seen or fixed.
export const ordersPresentValidation = (
  ordersPresent: boolean | null | undefined,
  allowTranscriptOrders: boolean,
) =>
  allowTranscriptOrders && (ordersPresent ?? null) === null
    ? 'Please select an option'
    : null

export const ordersCompleteValidation = (
  values: EventFeedback | undefined,
  allowTranscriptOrders: boolean,
) => {
  if (!allowTranscriptOrders || values?.orders_present !== true) {
    return null
  }
  const orders = Object.values(values.attendee_orders ?? {})
  if (orders.some(value => value === EXPEDITED_PENDING)) {
    return 'Select a turnaround for each expedited order.'
  }
  const hasOrder = orders.some(
    value => value !== null && value !== '' && value !== EXPEDITED_PENDING,
  )
  return hasOrder ? null : 'Add at least one transcript order'
}

export const nameValidation = (name: string | null) => {
  if (name === null || name.length === 0) {
    return 'Please enter name'
  }

  // every word in name must contain an uppercase letter, or be & symbol
  const nameWordPattern = /(^&$)|\p{Uppercase_Letter}/u
  if (
    !name
      .trim()
      .split(/\s+/)
      .every(w => nameWordPattern.test(w))
  ) {
    return 'Please enter correctly capitalized name'
  }

  return null
}

export const emailValidation = (email: string | null) => {
  if (email === null || email.length === 0) {
    return null
  }

  if (!isValidEmail(email!)) {
    return 'Invalid email'
  }
}

export const uniqueEmailValidation = (
  email: string | null,
  values: EventFeedback,
) => {
  const formatError = emailValidation(email)

  if (formatError) {
    return formatError
  }

  const isNotUnique =
    values.attendees.filter(a => (email ? a.email === email : false)).length > 1

  if (isNotUnique) {
    return 'Email is already in use'
  }

  return null
}

export const witnessLocationValidation =
  (isRequired: boolean, label: 'City' | 'State') =>
  (value: string | null | undefined, values: unknown, key: string) => {
    if (!isRequired) {
      return null
    }

    const parts = key.split('.')
    const attendee = (values as ReporterFeedback).events_feedbacks[
      parseInt(parts[1])
    ]?.attendees?.[parseInt(parts[3])]

    if (attendee?.role !== AttendeeRole.WITNESS) {
      return null
    }

    return value?.trim() ? null : `${label} is required`
  }

export const roleValidation = (role: AttendeeRole | null) => {
  if (!role || !Object.values(AttendeeRole).includes(role)) {
    return 'Please pick a role'
  }

  return null
}

export const presentValidation = (present: boolean | null) => {
  if (present === null) {
    return 'Pick one'
  }

  return null
}

// Attendance always renders the announce question; required regardless of cr_joined.
export const announceRecordValidation = (value: boolean | null) =>
  value === null ? 'Please select an option' : null
